import { isInUIC, validateIPv4 } from '../../../utils/common';
import { LogFields, LogTypeEnum } from '@Types/log';
import { t } from 'i18next';
import logConfig, {
  agentConnectionActions,
  idSyncActions,
  logArrayFields,
  logCopyFields,
  mappingFields,
  MISDIRECTED_DETECT_STATUS_DEFAULT,
  MISDIRECTED_SECURITY_FILTER_NAME,
  policyActions,
  provisionActions,
  scheduleSyncActions,
  timeFields,
} from '../constants';
import {
  getApiIntegrationSecurityRiskName,
  handleVASecurityRiskName,
  wrapLogFieldFuncMap,
} from '../shared';
import { MappingField, TimeField } from '../type';
import dayjs from 'dayjs';
import {
  constructSecurityRiskName,
  mapRiskAiDetectedBy,
  riskAiName,
} from '../../Quarantine/RiskAI/utils';
import { globalStoreGetter } from '../../../utils/globalStoreGetter';

const handleMissingContentListValue = (contentList: any[]) => {
  const isV1ecsUser = isInUIC();
  const v1ecsUser = t('log.auditLog.v1ecsUser');
  const casUser = t('log.auditLog.casUser');
  try {
    for (const item of contentList) {
      const user = item['user'];
      if (!user) {
        item['user'] = isV1ecsUser
          ? v1ecsUser.toLowerCase()
          : casUser.toLowerCase();
      } else if (
        user &&
        typeof user === 'string' &&
        user.toLowerCase() === 'cloud app security' &&
        isV1ecsUser
      ) {
        item['user'] = t('log.auditLog.v1ecsUser').toLowerCase();
      }
    }
  } catch (e) {
    console.error('handleMissingContentListValue error: ' + JSON.stringify(e));
  }
};

const parseAutomationAuditLogMsg = (contentList: any[]) => {
  try {
    for (const item of contentList) {
      if (
        item.action &&
        item.action.indexOf('AUTOMATION_API_AUTHENTICATION_TOKEN') > -1
      ) {
        if (item['message'].indexOf('API Type') > -1) {
          const apiTypeMessage = item['message'].split('API Type:');
          item['message'] =
            apiTypeMessage[0] +
            'API Type: ' +
            parseAuditMsgForAutomation(apiTypeMessage[1]);
        } else {
          item['message'] = parseAuditMsgForAutomation(item['message']);
        }
      }

      if (
        item.action &&
        item.action.indexOf('UPDATE_ADVANCED_SPAM_PROTECTION_DETECTION_LEVEL') >
          -1
      ) {
        const detectionLevelMessage = item['message'].split(': ');
        item['message'] =
          detectionLevelMessage[0] +
          ': ' +
          parseAuditMsgForPolicyDetectionLevel(detectionLevelMessage[1]);
      }

      if (item.action && item.action.indexOf('_APPLY_TO') > -1) {
        const applyToMessage = item['message'].split(': ');
        item['message'] =
          applyToMessage[0] +
          ': ' +
          parseAuditMsgForPolicyApplyTo(applyToMessage[1]);
      }
    }
  } catch (e) {
    console.error(e);
  }
};

const parseAuditMsgForAutomation = (scope: any) => {
  try {
    const auditLogMap = JSON.parse(scope);
    let fullMessage = '';
    for (const x in auditLogMap) {
      const auditLogInnerMap = auditLogMap[x];
      for (const auditLogInner in auditLogInnerMap) {
        const auditLogScopeMap = auditLogInnerMap[auditLogInner];
        if (auditLogInner === 'intelligent') {
          if (fullMessage !== '') fullMessage += ';';
          fullMessage += t('administration.apiTokenManagement.investigation');
        }
        if (auditLogInner === 'remediation') {
          if (fullMessage !== '') fullMessage += ';';
          fullMessage += t('administration.apiTokenManagement.remediation');
        }
        for (const auditLogScope in auditLogScopeMap) {
          if (auditLogInner === 'sweeping' && auditLogScope === 'mails') {
            if (fullMessage !== '') fullMessage += ';';
            fullMessage += t(
              'administration.apiTokenManagement.sweepingForMail'
            );
          }
          if (auditLogInner === 'siem' && auditLogScope === 'security_events') {
            if (fullMessage !== '') fullMessage += ';';
            fullMessage += t('administration.apiTokenManagement.siemForLog');
          }
          if (auditLogInner === 'mitigation' && auditLogScope === 'mails') {
            if (fullMessage !== '') fullMessage += ';';
            fullMessage += t(
              'administration.apiTokenManagement.mitigationForMail'
            );
          }
          if (auditLogInner === 'mitigation' && auditLogScope === 'accounts') {
            if (fullMessage !== '') fullMessage += ';';
            fullMessage += t(
              'administration.apiTokenManagement.mitigationForAccount'
            );
          }
        }
      }
    }
    return fullMessage;
  } catch (e) {
    return scope;
  }
};

const parseAuditMsgForPolicyDetectionLevel = (level: string) => {
  level = level.trim();
  if (level === '6') {
    level = t('policy.high');
  } else if (level === '4') {
    level = t('policy.medium');
  } else if (level === '2') {
    level = t('policy.low');
  }

  return level;
};

const parseAuditMsgForPolicyApplyTo = (message: string) => {
  message = message.trim();
  if (message === '7') {
    message = t('policy.allMails');
  } else if (message === '1') {
    message = t('policy.incomingMails');
  }

  return message;
};

const taskStringSplitOnce = (input: string) => {
  input = input.toLowerCase();
  if (input.indexOf('task') === 0) {
    return 'task.' + input.split('.').slice(1).join('.');
  } else {
    return input;
  }
};

const contentListPreHandle = (contentList: any[], logType: LogTypeEnum) => {
  if (logType === LogTypeEnum.ADUIT) {
    handleMissingContentListValue(contentList);
    parseAutomationAuditLogMsg(contentList);
  }
  const orgsMap = globalStoreGetter.getOrgsMap();
  contentList.forEach(function (_item) {
    const orgId = _item['companyGuid'];
    _item['orgName'] =
      orgsMap.get(orgId) || t('common.hints.deleted') + ': ' + orgId;

    if (
      _item['scanSource'] === 'Exchange Online' ||
      _item['scanSource'] === 'gmail'
    ) {
      _item['mailDirection'] = '';
    }

    if (_item.action === 'Apply Sensitivity Label' && _item.mipLabelName) {
      _item.action =
        t('quarantine.action.' + _item.action) + ': ' + _item.mipLabelName;
    }

    if (_item.MipBackupActionInfo) {
      _item.action =
        t('quarantine.action.' + _item.action) +
        ': ' +
        t('quarantine.mipBackupActionInfo');
    }

    if (_item.mipSkipOverrideLabel) {
      _item.action =
        t('quarantine.action.' + _item.action) +
        ': ' +
        t('quarantine.mipSkipOverrideLabel');
    }

    if (_item.mipSkipUnsupportedFile) {
      _item.action =
        t('quarantine.action.' + _item.action) +
        ': ' +
        t('quarantine.mipSkipUnsupportedFile');
    }

    if (_item.chatPassByModelB) {
      _item.action =
        t('quarantine.action.' + _item.action) +
        ': ' +
        t('quarantine.chatPassByModelB');
    }

    if (_item.passSanitizeForEncryptedMail) {
      _item.action =
        t('quarantine.action.' + _item.action) +
        ': ' +
        t('quarantine.passSanitizeForEncryptedMail');
    }
    if (_item.passReplaceForEncryptedMail) {
      _item.action =
        t('quarantine.action.' + _item.action) +
        ': ' +
        t('quarantine.passReplaceForEncryptedMail');
    }
    if (_item.filePathInZip) {
      _item.fileName = _item.fileName + _item.filePathInZip;
    }
    if (_item.msgMimeSender && _item.msgMimeSender instanceof Array) {
      _item.msgMimeSender = _item.msgMimeSender.join('; ');
    } else {
      _item.msgMimeSender = _item.msgMimeSender || '';
    }

    if (_item.scanSource && _item.scanSource === 'Exchange Server') {
      _item.internetMessageId = _item.messageId;
    }

    if (
      !(
        _item['totalProcessingTime'] &&
        _item['scanType'] === 'Realtime Scan' &&
        _item['scanSource'] === 'Exchange Online'
      )
    ) {
      _item['totalProcessingTime'] = ''; //Filter some data
    }
    if (
      _item.dlpMatchedLabels &&
      _item.dlpMatchedLabels.length !== 0 &&
      _item.dlpTemplate === undefined
    ) {
      _item.dlpTemplate = [];
    }
    if (
      _item.dlpMatchedLabels &&
      _item.dlpMatchedLabels.length !== 0 &&
      _item.dlpTemplate
    ) {
      _item.dlpTemplate = _item.dlpTemplate.concat(_item.dlpMatchedLabels);
    }

    if (!_item.protectionMode) {
      _item.protectionMode = t('quarantine.protectionMode.API');
    }

    const action = _item['action'] || '';

    if (action === '') {
      return;
    }
    const pattern = /^[A-Z_]+_SCHEDULED_SYNC$/;
    if (
      policyActions
        .concat(
          provisionActions,
          idSyncActions,
          scheduleSyncActions,
          agentConnectionActions
        )
        .indexOf(_item.action) !== -1
    ) {
      if (orgsMap.size > 1) {
        if (
          pattern.test(action) ||
          action.indexOf('INLINE_') !== -1 ||
          action === 'MIGRATE_OUTBOUND' ||
          action === 'UPGRADE_FOR_OUTBOUND'
        ) {
          _item.message = `(${_item.orgName}) ${t(
            taskStringSplitOnce(_item.message)
          )}`;
        } else {
          _item.message = `(${_item.orgName}) ${_item.message}`;
        }
      } else {
        if (
          pattern.test(action) ||
          action.indexOf('INLINE_') !== -1 ||
          action === 'MIGRATE_OUTBOUND' ||
          action === 'UPGRADE_FOR_OUTBOUND'
        ) {
          _item.message = t(taskStringSplitOnce(_item.message));
        }
      }
    } else if (
      pattern.test(action) ||
      action.indexOf('INLINE_') !== -1 ||
      action === 'MIGRATE_OUTBOUND' ||
      action === 'UPGRADE_FOR_OUTBOUND'
    ) {
      _item.message = t(taskStringSplitOnce(_item.message));
    }

    if (action === 'LOGON') {
      if (validateIPv4(_item.message)) {
        _item.message = `${t('log.logonIP')}: ${_item.message}`;
      } else {
        _item.message = '';
      }
    }
  });
};

function ensureArrayFieldValue(logItem: any) {
  for (const field of logArrayFields) {
    const value = logItem[field];
    if (value && !Array.isArray(value)) {
      logItem[field] = [value] as [string] | [number];
    }
  }
}

const constructContentListItem = (
  logItem: any,
  logType: LogTypeEnum,
  fields: Pick<LogFields, 'showFields' | 'mergedFields' | 'wrapFields'>
): Record<string, string> => {
  const ret: Record<string, any> = {};
  const { showFields, mergedFields, wrapFields } = fields;
  const offset = globalStoreGetter.getUtcOffset();

  ensureArrayFieldValue(logItem);

  logCopyFields.forEach(([from, to]) => {
    ret[to] = logItem[from];
  });

  showFields.forEach((showField) => {
    let keyField = '';

    // if not merge field, keyField === showField
    if (mergedFields[showField]) {
      // 针对 apiIntegration log 和 mergedSecurityRiskName 这一列的特殊处理
      if (
        logType === 'apiIntegration' &&
        showField === 'mergedSecurityRiskName'
      ) {
        const showValue = getApiIntegrationSecurityRiskName(logItem, t);
        keyField = showField;
        ret['mergedSecurityRiskName'] = showValue;
      } else {
        const mergedSubFields =
          logType === LogTypeEnum.QUARANTINE &&
          showField === 'mergedFieldFoundIn' &&
          logItem['scanSource'] === 'Exchange Online'
            ? ['folderPath', 'fileOriginalPath']
            : (mergedFields[showField] as string[]);
        mergedSubFields.forEach((mergedSubField) => {
          if (
            logItem['tm_security_filter_name'] === 'Advanced Spam Protection' &&
            showField === 'mergedSecurityRiskName' &&
            mergedSubField !== 'spamCategory'
          ) {
            return;
          }

          if (logItem[mergedSubField]) {
            // 找到第一个有值的字段
            if (keyField === '') {
              keyField = mergedSubField;
              ret[mergedSubField] = logItem[mergedSubField];
            }
          }

          if (
            logItem['tm_security_filter_name'] === 'Advanced Spam Protection' &&
            showField === 'mergedWritingStyleCompromisedSender'
          ) {
            //copy original key value
            ret[mergedSubField] = logItem[mergedSubField];
          }
        });
      }
    } else {
      keyField = showField;
    }

    if (!keyField) {
      ret[showField] = '';
      return;
    }

    // ensure all filed at least '', not undefined
    if (!logItem[keyField]) {
      ret[showField] = logItem[keyField] ?? '';
      return;
    }

    ret[showField] = logItem[keyField];

    // time field handle
    if (timeFields.indexOf(keyField as TimeField) >= 0) {
      ret[showField] = dayjs
        .utc(logItem[keyField as TimeField])
        .utcOffset(offset)
        .format(t('common.momentDateFormat'));
    }
    // wrap fields handle, 出来之后字段全都是 string 了
    if (
      wrapFields.indexOf(keyField) >= 0 &&
      wrapLogFieldFuncMap.get(keyField)
    ) {
      ret[showField] = (
        wrapLogFieldFuncMap.get(keyField) as (row: any) => string
      )(logItem);
    }

    //i18n mapping fields handle
    if (mappingFields.indexOf(keyField as MappingField) >= 0) {
      ret[showField] = t(
        `quarantine.${keyField}.${logItem[keyField]}`,
        logItem[keyField]
      ) as string;
    }

    if (
      logType === 'quarantine' &&
      logItem['filterType'] === 'Data Loss Prevention' &&
      showField === 'mergedSecurityRiskName'
    ) {
      //fix case 7102, DLP log should not show security risk name
      ret[showField] = '';
    }
  });

  // CAS-4153: Misdirected Emails post-processing.
  // The Security Risk Name column has no semantic sub-fields for this filter
  // (fileName/blockReason carry message metadata that doesn't describe the
  // detection), so always render the translated Security Filter value here.
  // When the backend omits mdDetectStatus, default to OnHold per spec.
  const isMisdirected =
    logItem['securityFilterName'] === MISDIRECTED_SECURITY_FILTER_NAME;
  if (isMisdirected) {
    ret['mergedSecurityRiskName'] = t(
      'log.misdirectedDetail.linkLabel'
    ) as string;
    const rawStatus = logItem['mdDetectStatus'];
    if (rawStatus === undefined || rawStatus === null || rawStatus === '') {
      ret['mdDetectStatus'] = t(
        `quarantine.mdDetectStatus.${MISDIRECTED_DETECT_STATUS_DEFAULT}`
      ) as string;
      ret['mdDetectStatusKey'] = MISDIRECTED_DETECT_STATUS_DEFAULT;
    }
  } else if (riskAiName.includes(ret['securityFilterNameKey'])) {
    // handle risk ai string mappings and redirect link
    ret['mergedSecurityRiskName'] = constructSecurityRiskName(
      ret.raiMatchedRules?.[0]?.id,
      ret.raiMatchedRules?.[0]?.name,
      t
    );
    // Map 'Risk AI' to translated string
    ret.blockReason = [t('riskAi.title')];
    ret['securityFilterName'] = t('riskAi.title');

    ret['malwareDetectedBy'] = mapRiskAiDetectedBy(
      logItem.malwareDetectedBy,
      t
    );
  }

  handleVASecurityRiskName(ret);
  return ret;
};

export const renderData = (contentList: any[], logType: LogTypeEnum) => {
  contentListPreHandle(contentList, logType);

  const res: any[] = [];

  contentList.forEach((_item: any) => {
    res.push(constructContentListItem(_item, logType, logConfig[logType]));
  });
  return res;
};
